import { useSocket } from 'context/SocketContext';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { adminApi } from 'redux/features/admin/adminApi';
import { employeeLeaveApi } from 'redux/features/employeeLeaveApi';
import { timeLogApi } from 'redux/features/timeLogApi';

const SocketEventsListener = () => {
  const socket = useSocket();
  const dispatch = useDispatch();
  const getLeaveYear = (leave) => {
    return new Date(leave.start_date_time).getFullYear();
  };

  useEffect(() => {
    if (!socket) return;
    console.log('socket connected');
    const handleApproved = (data) => {
      const year = getLeaveYear(data);

      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeavesByYear', { year }, (draft) => {
          const leave = draft.find((l) => l.id === data.id);
          if (leave) {
            leave.status = 'APPROVED';
          }
        }),
      );

      handleLeaveStateUpdated({ ...data, status: 'APPROVED' });
    };

    const handleRejected = (data) => {
      const year = getLeaveYear(data);

      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeavesByYear', { year }, (draft) => {
          const leave = draft.find((l) => l.id === data.id);
          if (leave) {
            leave.status = 'REJECTED';
          }
        }),
      );

      handleLeaveStateUpdated({ ...data, status: 'REJECTED' });
    };

    const handleCreated = (data) => {
      const year = getLeaveYear(data);

      // 1️⃣ All employee leaves
      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeaves', undefined, (draft) => {
          if (!draft.leaveRequests.find((l) => l.id === data.id)) {
            draft.leaveRequests.unshift(data);
          }
        }),
      );

      // 2️⃣ Leaves by year (draft = array)
      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeavesByYear', { year }, (draft) => {
          if (!draft.find((l) => l.id === data.id)) {
            draft.unshift(data);
          }
        }),
      );

      handleLeaveStateUpdated(data);
    };

    const handleUpdated = (data) => {
      const year = getLeaveYear(data);

      // 1️⃣ Update all leaves
      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeaves', undefined, (draft) => {
          const index = draft.leaveRequests.findIndex((l) => l.id === data.id);
          if (index !== -1) draft.leaveRequests[index] = { ...draft.leaveRequests[index], ...data };
        }),
      );

      // 2️⃣ Update yearly leaves
      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeavesByYear', { year }, (draft) => {
          const index = draft.findIndex((l) => l.id === data.id);
          if (index !== -1) draft[index] = { ...draft[index], ...data };
        }),
      );

      handleLeaveStateUpdated(data);
    };

    const handleCancelled = (data) => {
      const year = getLeaveYear(data);

      // 1️⃣ Remove from all leaves
      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeaves', undefined, (draft) => {
          draft.leaveRequests = draft.leaveRequests.filter((l) => l.id !== data.id);
        }),
      );

      // 2️⃣ Remove from yearly array
      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeavesByYear', { year }, (draft) => {
          return draft.filter((l) => l.id !== data.id);
        }),
      );

      handleLeaveStateUpdated(data);
    };

    // 3️⃣ Update stats
    const handleLeaveStateUpdated = (data) => {
      const year = getLeaveYear(data);

      dispatch(
        employeeLeaveApi.util.updateQueryData('getEmployeeLeavesStats', { year }, (draft) => {
          switch (
            data.status // <-- FIXED HERE
          ) {
            case 'PENDING':
              draft.pending += 1;
              draft.total += 1;
              break;
            case 'APPROVED':
              draft.approved += 1;
              draft.pending -= 1;
              break;
            case 'REJECTED':
              draft.rejected += 1;
              draft.pending -= 1;
              break;
            case 'CANCELLED':
              draft.total -= 1;
              break;
          }
        }),
      );
    };
    const handleCheckInCheckedOut = (type) => {
      dispatch(
        adminApi.util.updateQueryData('getDashboardStatistics', undefined, (draft) => {
          if (draft?.data) {
            switch (type) {
              case 'checkedIn':
                draft.data.activeSession += 1;
                draft.data.inactiveSession -= 1;
                break;
              case 'checkedOut':
                draft.data.activeSession -= 1;
                draft.data.inactiveSession += 1;
                break;
            }
          }
        }),
      );
    };

    socket.on('leaveRequestApproved', handleApproved);
    socket.on('leaveRequestRejected', handleRejected);
    socket.on('leaveRequestCreated', handleCreated);
    socket.on('leaveRequestUpdated', handleUpdated);
    socket.on('leaveRequestCancelled', handleCancelled);
    socket.on('userCheckedIn', () => handleCheckInCheckedOut('checkedIn'));
    socket.on('userCheckedOut', () => handleCheckInCheckedOut('checkedOut'));

    const handleRecentActivity = (data) => {
      dispatch(
        adminApi.util.updateQueryData(
          'getRecentActivity',
          { page: 1, limit: 10, search: '' },
          (draft) => {
            if (draft?.data?.data) {
              draft.data.data.unshift(data);
              if (draft.data.total !== undefined) {
                draft.data.total += 1;
              }
            }
          },
        ),
      );
    };

    socket.on('recentActivity', handleRecentActivity);

    // Unplanned leaves / log requests changed (cron, leave or log request actions): refetch the
    // lists, pending-salary items and sidebar counts that are on screen
    const handleUnplannedChanged = () => {
      dispatch(employeeLeaveApi.util.invalidateTags(['EmployeeLeave']));
      dispatch(timeLogApi.util.invalidateTags(['logUpdateRequests', 'monthlyTimeLogs']));
    };
    socket.on('unplannedLeavesChanged', handleUnplannedChanged);

    return () => {
      socket.off('leaveRequestApproved', handleApproved);
      socket.off('leaveRequestRejected', handleRejected);
      socket.off('leaveRequestCreated', handleCreated);
      socket.off('leaveRequestUpdated', handleUpdated);
      socket.off('leaveRequestCancelled', handleCancelled);
      socket.off('userCheckedIn');
      socket.off('userCheckedOut');
      socket.off('recentActivity', handleRecentActivity);
      socket.off('unplannedLeavesChanged', handleUnplannedChanged);
    };
  }, [dispatch, socket]);
  return null;
};

export default SocketEventsListener;
